import { supabase } from "@/lib/supabase";
import { parseRpcOrFallback } from "@/lib/parse-rpc";
import {
  indexSummarySchema,
  checkerHealthSchema,
  indexTimelineSchema,
  EMPTY_INDEX_SUMMARY,
  EMPTY_CHECKER_HEALTH,
  type IndexSummary,
  type CheckerHealth,
  type IndexTargetRow,
  type IndexKind,
  type IndexTimelineEvent,
} from "@/types/indexation";

export const INDEX_PAGE_SIZE = 50;

// Unified Indexation reads dmca_targets: indexation columns + the DMCA columns +
// the nightly money metrics (m_*, migration 20261021100000). index_status is the
// stored column last_index_status, aliased so the row type stays stable.
const TARGET_COLS =
  "id,kind,target,domain,tier,brand,geo,clicks," +
  "index_status:last_index_status,index_checked_at,index_dropped_since," +
  "last_status,last_notices,last_notice_ids,url_present,flagged_since,error_streak," +
  "m_clicks,m_revenue,m_profit,m_conversions,m_leads,m_sales,m_metrics_at";

// dmca_* tables + get_index_summary/get_dmca_summary aren't in the generated
// Database types. Structural escape hatches, same spirit as DmcaService.
interface LooseResult {
  data: unknown;
  error: { message: string } | null;
  count?: number | null;
}
interface LooseOrder {
  ascending?: boolean;
  nullsFirst?: boolean;
}
interface LooseQuery extends Promise<LooseResult> {
  select: (cols: string, opts?: { count?: "exact" }) => LooseQuery;
  eq: (col: string, val: unknown) => LooseQuery;
  is: (col: string, val: unknown) => LooseQuery;
  in: (col: string, vals: readonly unknown[]) => LooseQuery;
  gte: (col: string, val: unknown) => LooseQuery;
  lte: (col: string, val: unknown) => LooseQuery;
  ilike: (col: string, val: string) => LooseQuery;
  order: (col: string, opts?: LooseOrder) => LooseQuery;
  range: (from: number, to: number) => LooseQuery;
  limit: (n: number) => LooseQuery;
}
const looseFrom = (table: string): LooseQuery =>
  (supabase.from as unknown as (t: string) => LooseQuery)(table);
const looseRpc = (fn: string, args: Record<string, unknown> = {}): Promise<LooseResult> =>
  (supabase.rpc as unknown as (fn: string, args: Record<string, unknown>) => Promise<LooseResult>)(
    fn,
    args,
  );

export type IndexStatusFilter = "all" | "indexed" | "not_indexed" | "unchecked";
export type IndexKindFilter = "all" | IndexKind;
export type IndexTierFilter = "all" | 1 | 2 | 3;
// "flagged" = has a takedown (last_status='dmca'); "none" = checked and clean
// (last_status='clean'), i.e. no takedown — pair with Not indexed to isolate the
// organic / non-DMCA deindexing that needs investigating.
export type IndexDmcaFilter = "all" | "flagged" | "none";
// Event-in-range table filter: restrict to targets whose current episode STARTED
// inside [from,to] — "dropped" uses index_dropped_since, "flagged" flagged_since.
export type IndexEventFilter = "dropped" | "flagged";

// Sortable grid columns. These are the grid's column ids too (the grid header
// clicks map 1:1), and each maps to one STORED dmca_targets column below — so
// sort stays server-side and the value never becomes user-controlled free text.
export type IndexSortCol =
  | "target"
  | "brand"
  | "index_status"
  | "dmca_status"
  | "tier"
  | "clicks"
  | "revenue"
  | "conversions"
  | "registrations"
  | "deposits"
  | "money"
  | "notices"
  | "flagged_since"
  | "dropped_since"
  | "last_checked";

const SORT_COLUMN: Record<IndexSortCol, string> = {
  target: "target",
  brand: "brand",
  index_status: "last_index_status",
  dmca_status: "last_status",
  tier: "tier",
  clicks: "m_clicks",
  revenue: "m_revenue",
  conversions: "m_conversions",
  registrations: "m_leads",
  deposits: "m_sales",
  money: "m_profit",
  notices: "last_notices",
  flagged_since: "flagged_since",
  dropped_since: "index_dropped_since",
  last_checked: "index_checked_at",
};

const isSortCol = (c: string): c is IndexSortCol =>
  Object.prototype.hasOwnProperty.call(SORT_COLUMN, c);

export interface IndexTargetsParams {
  kind: IndexKindFilter;
  status: IndexStatusFilter;
  tier: IndexTierFilter;
  /** DMCA verdict filter. "flagged" = last_status = 'dmca'. */
  dmca?: IndexDmcaFilter;
  /** VIP-only: restrict to the pinned-override targets. vipIds is the current
   *  active-override id set (a handful); empty + vipOnly → no rows, correctly. */
  vipOnly?: boolean;
  vipIds?: number[];
  /** Event-in-range filter (set by the flow cards). Uses from/to against the
   *  matching marker column. Null = the range does not touch the table. */
  eventType?: IndexEventFilter;
  from?: string | null;
  to?: string | null;
  geo: string | null;
  search: string;
  /** Ordered sort keys (locked first, then by recency — the grid's engine
   *  orders them). Each becomes one server .order(); [] = the default order. */
  sorts?: { col: IndexSortCol; dir: "asc" | "desc" }[];
  page: number;
  pageSize?: number;
}

class IndexationService {
  private static instance: IndexationService;
  private constructor() {}
  public static getInstance(): IndexationService {
    if (!IndexationService.instance) IndexationService.instance = new IndexationService();
    return IndexationService.instance;
  }

  public async getSummary(from?: string | null, to?: string | null): Promise<IndexSummary> {
    const { data, error } = await looseRpc("get_index_summary", {
      p_from: from ?? null,
      p_to: to ?? null,
    });
    if (error) throw new Error(error.message);
    return parseRpcOrFallback(indexSummarySchema, data, EMPTY_INDEX_SUMMARY, "get_index_summary");
  }

  /** One target's index+DMCA transition history (get_target_timeline), already
   *  collapsed to change-points. Whole result as one small jsonb array. */
  public async getTargetTimeline(
    targetId: number,
    from?: string | null,
    to?: string | null,
  ): Promise<IndexTimelineEvent[]> {
    const { data, error } = await looseRpc("get_target_timeline", {
      p_id: targetId,
      p_from: from ?? null,
      p_to: to ?? null,
    });
    if (error) throw new Error(error.message);
    const parsed = indexTimelineSchema.safeParse(data);
    return parsed.success ? parsed.data : [];
  }

  /** "Is the DMCA checker alive?" — last_check_at / checks_24h / errors_24h off
   *  get_dmca_summary. Read here (not via DmcaService) to keep this zone whole. */
  public async getCheckerHealth(): Promise<CheckerHealth> {
    const { data, error } = await looseRpc("get_dmca_summary");
    if (error) throw new Error(error.message);
    return parseRpcOrFallback(checkerHealthSchema, data, EMPTY_CHECKER_HEALTH, "get_dmca_summary");
  }

  public async getTargets(
    p: IndexTargetsParams,
  ): Promise<{ rows: IndexTargetRow[]; total: number }> {
    const pageSize = p.pageSize ?? INDEX_PAGE_SIZE;
    const from = (p.page - 1) * pageSize;
    const to = from + pageSize - 1;

    let q = looseFrom("dmca_targets").select(TARGET_COLS, { count: "exact" }).eq("active", true);

    if (p.kind !== "all") q = q.eq("kind", p.kind);
    if (p.tier !== "all") q = q.eq("tier", p.tier);
    if (p.status === "unchecked") q = q.is("last_index_status", null);
    else if (p.status !== "all") q = q.eq("last_index_status", p.status);
    if (p.dmca === "flagged") q = q.eq("last_status", "dmca");
    else if (p.dmca === "none") q = q.eq("last_status", "clean");
    if (p.vipOnly) q = q.in("id", p.vipIds ?? []);
    // Event-in-range: the picked date range filters the table only when a flow
    // card set an event type. The onset marker must be non-null (not-null filter
    // is implicit via gte), and we bound whichever end is set.
    if (p.eventType) {
      const col = p.eventType === "dropped" ? "index_dropped_since" : "flagged_since";
      // gte on the marker also drops null markers, so "has this event" is implied.
      // All-time (no from) floors at 2020-01-01, matching the date-utils all_time.
      q = q.gte(col, p.from ?? "2020-01-01T00:00:00Z");
      if (p.to) q = q.lte(col, p.to);
    }
    if (p.geo) q = q.eq("geo", p.geo);
    if (p.search) q = q.ilike("target", `%${p.search}%`);

    const sorts = (p.sorts ?? []).filter((s) => isSortCol(s.col));
    if (sorts.length > 0) {
      // Each rule is one server .order(), in the engine's order (locked first).
      // Nulls last in both directions — an empty metric is not "small" — to match
      // the client engine's comparator so sort reads the same everywhere.
      for (const s of sorts) {
        q = q.order(SORT_COLUMN[s.col], { ascending: s.dir === "asc", nullsFirst: false });
      }
    } else {
      q = q.order("tier", { ascending: true }).order("m_clicks", { ascending: false, nullsFirst: false });
    }
    const { data, error, count } = await q.range(from, to);
    if (error) throw new Error(error.message);
    return { rows: (data as IndexTargetRow[]) ?? [], total: count ?? 0 };
  }

  /** Active VIP target ids (dmca_overrides). A handful of rows — read once and
   *  cached; the grid marks the VIP column from the set and the VIP-only filter
   *  passes it back as `.in('id', …)`. Kept here (not DmcaService) to stay in zone. */
  public async getVipTargetIds(): Promise<number[]> {
    const { data, error } = await looseFrom("dmca_overrides").select("target_id").eq("active", true);
    if (error) throw new Error(error.message);
    return ((data as { target_id: number }[]) ?? []).map((r) => Number(r.target_id));
  }

  /** URL targets under a domain — fetched lazily when a domain row expands.
   *  m_* are exact per url, so no roll-up here. Biggest earners first. */
  public async getUrlChildren(domain: string): Promise<IndexTargetRow[]> {
    const { data, error } = await looseFrom("dmca_targets")
      .select(TARGET_COLS)
      .eq("kind", "url")
      .eq("domain", domain)
      .eq("active", true)
      .order("m_clicks", { ascending: false, nullsFirst: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return (data as IndexTargetRow[]) ?? [];
  }
}

export const indexationService = IndexationService.getInstance();
